import { zodResolver } from "@hookform/resolvers/zod";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { Button, Icon, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateClientProfileMutation } from "@/redux/apis/clientProfile/clientProfileApi";
import { ClientProfileNotificationEmails, NotificationEmailCategory, NotificationEmailPair } from "@/redux/apis/clientProfile/types";
import { addToast } from "@/utils";
import { NotificationCategoryRow } from "./NotificationCategoryRow";
import { CATEGORIES, emailNotificationsSchema, EmailNotificationsFormData } from "./fixtures";

export interface EmailNotificationPreferenceCardProps {
  value: ClientProfileNotificationEmails;
  /** The account's login email/username — shown in the info banner (read-only). */
  loginEmail?: string | null;
  accountId?: string;
}

// Normalize a row's value into the persisted shape: "" → null, keep null hidden-secondary as null.
const normalizePair = (pair: NotificationEmailPair): NotificationEmailPair => ({
  primary: pair.primary ? pair.primary : null,
  secondary: pair.secondary ? pair.secondary : null,
});

const toDefaultValues = (value: ClientProfileNotificationEmails): EmailNotificationsFormData =>
  CATEGORIES.reduce((acc, { key }) => {
    acc[key] = {
      // Only the category's OWN primary — never prefill the default. An empty category
      // saves as null and the backend resolver falls back to the default at send time.
      // Prefilling here would persist the default into every category, pinning them so a
      // later default-email change no longer propagates.
      primary: value[key]?.primary || "",
      // Keep `null` so the row hides the secondary input until "Add another email".
      // The schema only accepts "" | email, so `null` is coerced to "" inside the resolver below.
      secondary: (value[key]?.secondary ?? null) as EmailNotificationsFormData[NotificationEmailCategory]["secondary"],
    };
    return acc;
  }, {} as EmailNotificationsFormData);

// The "Add another email" row keeps a hidden secondary as `null`, but the schema only allows
// "" | email. Coerce each pair's null fields to "" before zod validates, so a hidden secondary
// never blocks a save.
const baseResolver = zodResolver(emailNotificationsSchema);
const resolver: Resolver<EmailNotificationsFormData> = (values, context, options) => {
  const coerced = CATEGORIES.reduce((acc, { key }) => {
    const pair = values?.[key] ?? {};
    acc[key] = { primary: pair.primary ?? "", secondary: pair.secondary ?? "" };
    return acc;
  }, {} as EmailNotificationsFormData);
  return baseResolver(coerced, context, options);
};

export const EmailNotificationPreferenceCard = ({ value, loginEmail, accountId }: EmailNotificationPreferenceCardProps) => {
  // Shared card: operator-managed view uses 3rd-person copy, client self-service keeps 2nd-person.
  const isOperatorView = !!accountId;
  const [isEditing, setIsEditing] = useState(false);
  const [updateClientProfile, { isLoading }] = useUpdateClientProfileMutation();

  const defaultValues = toDefaultValues(value);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailNotificationsFormData>({
    resolver,
    defaultValues,
  });

  const handleCancel = () => {
    reset(toDefaultValues(value));
    setIsEditing(false);
  };

  const onSubmit = handleSubmit(async (data) => {
    // Send all 5 categories — the backend treats each provided category as a full replace.
    const notification_emails = CATEGORIES.reduce((acc, { key }) => {
      acc[key] = normalizePair(data[key]);
      return acc;
    }, {} as ClientProfileNotificationEmails);

    try {
      await updateClientProfile({ accountId, notification_emails }).unwrap();
      addToast("success", "Email preferences updated");
      setIsEditing(false);
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((message) => addToast("danger", message));
    }
  });

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col rounded-lg border border-neutral-gray bg-white p-5 @container">
      <div className="flex items-start justify-between gap-4 pb-2">
        <Typography variant="title" className="text-neutral-silver-gray">
          Notification Email Preference
        </Typography>
        <div className="flex shrink-0 items-center gap-2">
          {isEditing ? (
            // Wide card only — on a narrow card the Save/Cancel buttons render full-width at the bottom (see below).
            <div className="hidden items-center gap-2 @[560px]:flex">
              <Button variant="secondary" size="sm" type="button" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
                Save
              </Button>
            </div>
          ) : (
            <Button variant="secondary" size="sm" type="button" endIcon="Edit2" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      </div>

      {loginEmail && (
        <div className="mb-5 flex flex-col gap-1 rounded-md bg-warning-light px-2 py-1 @[560px]:flex-row @[560px]:items-center @[560px]:gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-warning">
            <Icon name="LampCharge" variant="Bold" size={18} className="text-white" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <Typography variant="small" className="break-all text-neutral-dark-gray">
              {loginEmail}
            </Typography>
            <Typography variant="small-regular" className="!font-medium text-neutral-dark-gray">
              This is the account&apos;s login email and username. Updating notification emails below won&apos;t change how{" "}
              {isOperatorView ? "the client signs in" : "you sign in"}.
            </Typography>
          </div>
        </div>
      )}

      {/* The Default row (first) carries a bottom border, visually splitting it from the
          remaining categories. The other rows have no separators, matching the Figma. */}
      <div className="flex flex-col">
        {CATEGORIES.map(({ key, label, description }) => {
          // Divider only under the Default row.
          const wrapperClassName = key === "default" ? "border-b border-neutral-gray pb-6" : undefined;

          if (!isEditing) {
            return (
              <div key={key} className={wrapperClassName}>
                <NotificationCategoryRow
                  label={label}
                  description={description}
                  mode="display"
                  value={value[key]}
                  defaultPrimary={value.default?.primary}
                />
              </div>
            );
          }

          return (
            <div key={key} className={wrapperClassName}>
              <Controller
                control={control}
                name={key as NotificationEmailCategory}
                render={({ field }) => (
                  <NotificationCategoryRow
                    label={label}
                    description={description}
                    mode="edit"
                    value={field.value}
                    onChange={field.onChange}
                    error={{
                      primary: errors[key]?.primary?.message,
                      secondary: errors[key]?.secondary?.message,
                    }}
                  />
                )}
              />
            </div>
          );
        })}
      </div>

      {/* Narrow card only — full-width stacked actions at the bottom (matches Figma mobile edit frame).
          On a wide card the same actions live in the header. */}
      {isEditing && (
        <div className="mt-5 flex flex-col gap-2 @[560px]:hidden">
          <Button variant="primary" size="md" type="submit" className="w-full" disabled={isLoading}>
            Save
          </Button>
          <Button variant="secondary" size="md" type="button" className="w-full" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      )}
    </form>
  );
};
